import { ClientError } from "graphql-request";
import { ErrorADT, safe } from "pb.safe";

import { $, query } from "../../../../schema.dev-sdk-api-appsync.generated";
import type { PresetOrString } from "./preset-or-string";
import type { ApiClient } from "./shared/api-client";

export namespace getDeviceSettings {
	export type DeviceSettings = {
		id: string;
		name: string;
		entity: {
			id: string;
		};
		terminal?: {
			id: string;
			name: string;
			model: PresetOrString<"T2">;
			color: string | undefined;
			logoUrl: string | undefined;
		};
	};
}

export async function getDeviceSettings({
	apiClient,
	deviceId,
}: {
	apiClient: ApiClient;
	deviceId: string;
}): Promise<
	undefined | getDeviceSettings.DeviceSettings | ErrorADT<"NetworkError">
> {
	const response = await safe(() =>
		apiClient.request(getDeviceSettings.gql.getDeviceSettings, {
			deviceUuid: deviceId,
		}),
	);
	if (response instanceof Error) {
		if (response instanceof ClientError) {
			const error = response.response.errors?.[0] as
				{ errorType?: string } | undefined;
			if (
				error?.errorType === "UNAUTHORIZED_ERROR" ||
				error?.errorType === "NOT_FOUND"
			) {
				return undefined;
			}
		}

		return ErrorADT("NetworkError");
	}

	const { name: deviceName } = response.getDeviceSettings;
	if (!deviceName) {
		return undefined;
	}

	const {
		entityUuid: entityId,
		site,
		posPairConfiguration,
	} = response.getDeviceSettings;
	const terminal = posPairConfiguration?.devices?.[0];
	return {
		id: deviceId,
		name: deviceName,
		entity: { id: entityId },
		terminal:
			terminal?.id && terminal?.name
				? {
						id: terminal.id,
						name: terminal.name,
						model: terminal.model ?? "T2",
						color: site?.screensaver?.primaryColour,
						logoUrl: site?.screensaver?.primaryLogoUrl,
					}
				: undefined,
	};
}

getDeviceSettings.gql = {
	getDeviceSettings: query("getDeviceSettings", (q) => [
		q.getDeviceSettings({ deviceUuid: $("deviceUuid") }, (c) => [
			c.name,
			c.entityUuid,
			c.site((c) => [
				c.screensaver((c) => [c.primaryColour, c.primaryLogoUrl]),
			]),
			c.posPairConfiguration((c) => [
				c.devices((c) => [c.id, c.name, c.model]),
			]),
		]),
	]),
};
